<template>
    <div v-show="state && state !== ViewState.SignOut" class="authenticator" :class="colorPalette">
        <slot />

        <div class="button-container">
            <alert v-if="err && err.keypath" :variant="ComponentState.Critical">
                <i18n-t :keypath="err.keypath" tag="div">
                    <template #email>
                        <b
                            ><i>{{ err.customData?.email }}</i></b
                        >
                    </template>
                    <template #code>
                        {{ err.code }}
                    </template>
                    <template #support>
                        <a :href="`mailto:${config.supportEmail}`" target="/">{{ $t(`common.link.support`) }}</a>
                    </template>
                </i18n-t>
            </alert>

            <div v-show="showForm">
                <form @submit.prevent="onSubmitForm">
                    <text-box
                        ref="emailInput"
                        v-model="email"
                        autocomplete="email"
                        type="email"
                        :label="$t('authenticator.email_address')"
                        placeholder="user@email.com"
                        required
                        show-input
                    />
                    <text-box
                        v-if="state === ViewState.CreateAccount"
                        ref="displayNameInput"
                        v-model="displayName"
                        autocomplete="displayName"
                        :label="t('authenticator.create_account.first_and_last_name')"
                        placeholder=""
                        required
                    />
                    <div v-if="state === ViewState.CreateAccount" class="subtext">
                        {{ $t(`authenticator.create_account.set_a_password_subtext`) }}
                    </div>
                    <text-box
                        v-if="state === ViewState.SignInWithPassword || state === ViewState.CreateAccount"
                        ref="passwordInput"
                        v-model="password"
                        autocomplete="current-password"
                        :minlength="6"
                        :label="$t(`authenticator.${state}.password`)"
                        type="password"
                        required
                    />
                    <text-box
                        v-if="state === ViewState.CreateAccount"
                        ref="confirmPasswordInput"
                        v-model="confirmPassword"
                        :label="$t(`authenticator.create_account.confirm_your_password`)"
                        type="password"
                        required
                    />
                    <horizontal-stack>
                        <large-button v-show="state !== ViewState.SignIn" variant="secondary" @click="reset()">
                            {{ $t('common.button.cancel') }}
                        </large-button>
                        <large-button type="submit" :loading="isLoading">
                            {{ $t(`common.button.continue`) }}
                        </large-button>
                    </horizontal-stack>
                </form>
            </div>

            <vertical-stack v-if="state === ViewState.SignIn">
                <div class="eyebrow-heading">
                    {{ $t('common.button.or') }}
                </div>
                <large-button
                    v-for="provider in providers"
                    :key="provider.id"
                    class="provider-button"
                    :class="provider.name"
                    variant="primary"
                    @click.stop="onSignInWithProvider(provider.id)"
                >
                    <template #icon>
                        <img :src="provider.icon" />
                    </template>
                    {{ $t('common.button.continue_with_provider', { providerName: provider.name }) }}
                </large-button>

                <large-button v-if="err" variant="secondary" @click="reset()">
                    {{ $t('common.button.cancel') }}
                </large-button>
            </vertical-stack>
        </div>

        <div v-if="showAcceptLegalTerms" class="legal">
            <i18n-t keypath="authenticator.legal.accept_tos" tag="div">
                <template #tos>
                    <a :href="pathWithLocale('eula')">{{ $t('authenticator.legal.accept_tos_link') }}</a>
                </template>
                <template #pp>
                    <a :href="pathWithLocale('privacy')">{{ $t('authenticator.legal.accept_pp_link') }}</a>
                </template>
            </i18n-t>
        </div>

        <vertical-stack v-if="showTroubleshooting" class="paragraph-content">
            <div v-if="state === ViewState.AcceptInviteLinkResent">
                {{ $t(`authenticator.troubleshooting`) }}
            </div>
            <div v-else-if="state !== ViewState.SignInWithLinkError">
                {{ $t(`authenticator.troubleshooting_plural`) }}
            </div>
            <a v-if="state === ViewState.SignInWithPassword" href="/" @click.prevent="switchToRequestResetPasswordView">{{
                $t(`common.link.reset_password`)
            }}</a>
            <a v-if="state === ViewState.SignInWithPassword" href="/" @click.prevent="switchToSignInWithLinkView">{{
                $t(`common.link.sign_in_with_link`)
            }}</a>
            <confirmation-link
                v-if="state !== ViewState.SignInWithPassword && state !== ViewState.SignInWithLinkError"
                ref="linkComponent"
                @click="onResendEmail"
            >
                {{ $t(`common.link.resend_email`) }}
            </confirmation-link>
            <a
                v-if="
                    state !== ViewState.SignInWithPassword && state !== ViewState.ResetPasswordLinkSent && state !== ViewState.AcceptInviteLinkResent
                "
                href="/"
                @click.prevent="switchToExistingEmailPasswordView"
                >{{ $t(`common.link.sign_in_with_password`) }}</a
            >
            <a
                v-if="state !== ViewState.SignInWithPassword && state !== ViewState.SignInWithLink && state !== ViewState.AcceptInviteLinkResent"
                href="/"
                @click.prevent="switchToSignInWithLinkView"
                >{{ $t(`common.link.go_back_to_the_start`) }}</a
            >
        </vertical-stack>
    </div>
</template>

<script setup>
import { onMounted, ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';

import {
    LargeButton,
    TextBox,
    VerticalStack,
    HorizontalStack,
    usePalette,
    paletteProps,
    useAuthentication,
    useProduct,
    Alert,
    ConfirmationLink,
    AuthState as ViewState,
    ComponentState,
    useEnvironment,
    useI18n as usePathfinderI18n,
} from 'ihunter-pathfinder-components';

import ErrorNotice from './ErrorNotice.vue';
const { signInLink, clearSignInLink, SignInLinkType, ProductType } = useProduct();

const { t, te } = useI18n();

const {
    user,
    signInWithProvider,
    signInWithEmail,
    signInWithAcceptInviteLink,
    createUserAndSignInToSession,
    resendAcceptInviteLink,
    sendSignInLink,
    signInWithLink,
    validateSignInLink,
    sendResetPasswordLink,
} = useAuthentication();
const { isLoading, doLoadingOperation } = useLoadingState();
const { getMessagesWithPrefix, pathWithLocale } = usePathfinderI18n();
const { config } = useEnvironment();

const state = ref(null);
const linkComponent = ref(null);

const acceptInviteUser = ref(null);
const err = ref(null);
const testError = ref(null);

const emailInput = ref(null);
const displayNameInput = ref(null);
const passwordInput = ref(null);
const confirmPasswordInput = ref(null);

const email = ref('');
const displayName = ref('');
const password = ref('');
const confirmPassword = ref('');

const EMAIL_STORAGE_KEY = 'emailSignIn';

const props = defineProps({
    ...paletteProps(),
});

const emit = defineEmits(['state-changed']);

const { colorPalette } = usePalette(props);

import iconApple from '../images/apple.png';
import iconGoogle from '../images/google.svg';
import iconFacebook from '../images/facebook.svg';
import { useLoadingState } from './composables/useLoadingState';

onMounted(async () => {
    console.log(`Initialize Authenticator - user ${user.value?.uid}`);
    // focusField(emailInput);
    initializeTestErrorFromUrl();
});

watch(user, async (user) => {
    console.log(`Authenticator - user updated: ${user?.uid}`);
    const viewState =
        user && state.value !== ViewState.AcceptInviteLinkResent
            ? ViewState.SignOut
            : state.value === ViewState.SignOut
              ? ViewState.SignIn
              : state.value;
    setViewState(viewState);
});

const setAuthState = (authState) => {
    if (authState && state.value !== authState && Object.values(ViewState).includes(authState)) {
        console.log(`setAuthState: Setting authState to '${authState}'`);
        setViewState(authState);
    } else {
        console.warn(`setAuthState: Invalid authState specified ${authState}`);
    }
};

const setAuthEmail = (authEmail) => {
    if (authEmail && !email.value && authEmail !== email.value) {
        console.log(`setAuthEmail: Setting email to '${authEmail}'`);
        email.value = authEmail;
    } else {
        console.warn(`setAuthEmail: Invalid email specified ${authEmail}`);
    }
};

const checkSignInLink = async () => {
    try {
        setViewState(ViewState.ValidatingSignInLink);
        const emailForSignIn = localStorage.getItem(EMAIL_STORAGE_KEY);
        if (emailForSignIn) {
            console.log(`Setting email from local storage: ${emailForSignIn}`);
            email.value = emailForSignIn;
            localStorage.removeItem(EMAIL_STORAGE_KEY);
        }

        if (signInLink.value !== SignInLinkType.AcceptInvite && validateSignInLink()) {
            console.log(`Detected authentication sign-in link`);
            if (signInLink.value === SignInLinkType.NewAccount) {
                setViewState(ViewState.CreateAccount);
            } else if (emailForSignIn) {
                try {
                    console.log(`Automatically signing in with cached email ${emailForSignIn}`);
                    await signInWithLink(emailForSignIn);
                } catch (error) {
                    handleError(error);
                    setViewState(ViewState.SignInWithLinkError);
                }
            } else {
                setViewState(ViewState.SignInWithLink);
            }
        } else {
            setViewState(ViewState.SignIn);
        }
    } catch (error) {
        handleError(error);
        setViewState(ViewState.SignIn);
    }
};

defineExpose({ state, setAuthState, setAuthEmail, checkSignInLink });

const showForm = computed(() => {
    const excludeStates = [ViewState.ValidatingSignInLink, ViewState.SignInWithLinkError];
    return !linkSent.value && !excludeStates.includes(state.value);
});

const showTroubleshooting = computed(() => {
    return (
        linkSent.value ||
        (err.value && state.value !== ViewState.SignIn) ||
        state.value === ViewState.SignInWithLink ||
        state.value === ViewState.SignInWithLinkError ||
        state.value === ViewState.SignInWithPassword
    );
});

const showAcceptLegalTerms = computed(() => {
    const states = [ViewState.SignIn, ViewState.CreateAccount, ViewState.SignInWithLink, ViewState.SignInWithPassword];
    return states.includes(state.value);
});

const linkSent = computed(() => {
    const states = [ViewState.SignInLinkSent, ViewState.AcceptInviteLinkResent, ViewState.ResetPasswordLinkSent];
    return states.includes(state.value);
});

const supportedProviders = [
    {
        id: 'google.com',
        name: 'Google',
        icon: iconGoogle,
    },
    {
        id: 'apple.com',
        name: 'Apple',
        icon: iconApple,
    },
    {
        id: 'facebook.com',
        name: 'Facebook',
        icon: iconFacebook,
    },
];

// TODO-Pathfinder (HUNT-1777): app-wide strategy for focusing the first field
// const focusField = (fieldRef) => {
//   nextTick(() => {
//     if (fieldRef.value) {
//       fieldRef.value.focus();
//     }
//   });
// }

const setLinkState = (state, message) => {
    nextTick(() => {
        if (linkComponent.value) {
            linkComponent.value.setLinkState(state, message);
        }
    });
};

const setViewState = (viewState) => {
    if (state.value !== viewState) {
        console.log(`View state changed from '${state.value}' to '${viewState}' - user ${user.value?.uid}`);
        isLoading.value = false;
        state.value = viewState;

        //Possibility we want to reset the Link when you change ViewState? TestState=SignInWithLink > This might not work...
        if (viewState !== ViewState.SignInWithLinkError) {
            clearError();
        }
        emit('state-changed', viewState, email.value);
    }
};

const switchToSignInWithLinkView = () => {
    clearSignInLink();
    reset();
};

const switchToExistingEmailPasswordView = () => {
    setViewState(ViewState.SignInWithPassword);
    // eld(email.value ? passwordInput : emailInput);
};

const switchToRequestResetPasswordView = () => {
    setViewState(ViewState.RequestResetPassword);
    // focusField(emailInput);
};

const onResendEmail = async () => {
    if (state.value === ViewState.AcceptInviteLinkResent) {
        await doLoadingOperation(onResendAcceptInviteLink);
    } else if (state.value === ViewState.ResetPasswordLinkSent) {
        setLinkState(ComponentState.Loading);
        await doLoadingOperation(onRequestResetPassword);
    } else {
        setLinkState(ComponentState.Loading);
        await doLoadingOperation(onSendSignInLink);
    }
};

const onSendSignInLink = async () => {
    try {
        clearError();
        await sendSignInLink(email.value);
        localStorage.setItem(EMAIL_STORAGE_KEY, email.value);
        isLoading.value = false;
        setViewState(ViewState.SignInLinkSent);
        setLinkState(ComponentState.Success, t(`common.link.resend_email_success`));
        console.log(`Sent sign in link to ${email.value}`);
    } catch (error) {
        handleError(error);
        setLinkState(ComponentState.Error, t(`common.link.resend_email_failed`));
    }
};

const onResendAcceptInviteLink = async () => {
    setLinkState(ComponentState.Loading);

    try {
        clearError();
        await resendAcceptInviteLink(email.value);
        localStorage.setItem(EMAIL_STORAGE_KEY, email.value);
        setViewState(ViewState.AcceptInviteLinkResent);
        await nextTick();
        setLinkState(ComponentState.Success, t(`common.link.resend_email_success`));
        console.log(`Resent accept invite link to ${email.value}`);
    } catch (error) {
        handleError(error);
        await nextTick();
        setLinkState(ComponentState.Error, t(`common.link.resend_email_failed`));
    }
};

const onRequestResetPassword = async () => {
    try {
        clearError();
        await sendResetPasswordLink(email.value);
        setViewState(ViewState.ResetPasswordLinkSent);
        await nextTick();
        setLinkState(ComponentState.Success, t(`common.link.resend_email_success`));

        console.log(`Sent reset password link to ${email.value}`);
    } catch (error) {
        handleError(error);
        await nextTick();
        setLinkState(ComponentState.Error, t(`common.link.resend_email_failed`));
    }
};

const onSignInWithAcceptInviteLink = async () => {
    console.log(`Signing in with accept invite link`);
    acceptInviteUser.value = await signInWithAcceptInviteLink(email.value);
    if (!acceptInviteUser.value) {
        console.log(`Email does not match accept invite link or the link has already been used. Sending a new link`);
        await onResendAcceptInviteLink();
    } else if (acceptInviteUser.value.isNewUser) {
        console.log(`Enter display name for new account to accept invite`);
        setViewState(ViewState.CreateAccount);
        // focusField(displayNameInput);
    }
};

const onSubmitForm = async () => {
    clearError();

    try {
        let operationFn;

        if (signInLink.value === SignInLinkType.AcceptInvite) {
            if (state.value === ViewState.SignIn) {
                if (acceptInviteUser.value && acceptInviteUser.value.email === email.value) {
                    // User had already signed in but probably clicked the Back button
                    // We can use their previous credentials as they have re-entered the same email
                    console.log(`Enter display name for new account to accept invite`);
                    setViewState(ViewState.CreateAccount);
                    // focusField(displayNameInput);
                } else {
                    operationFn = onSignInWithAcceptInviteLink;
                }
            } else if (state.value == ViewState.CreateAccount && acceptInviteUser.value && validatePasswordFields()) {
                console.log(`Creating new email account and signing into session`);
                operationFn = createUserAndSignInToSession.bind(null, acceptInviteUser.value, displayName.value, password.value);
            }
        } else if (state.value === ViewState.SignIn) {
            operationFn = onSendSignInLink;
        } else if (state.value === ViewState.CreateAccount && validatePasswordFields()) {
            console.log(`Create new email account: ${email.value}`);
            operationFn = signInWithLink.bind(null, email.value, displayName.value, password.value);
        } else if (state.value === ViewState.SignInWithLink) {
            console.log(`Sign in with existing email: ${email.value}`);
            operationFn = signInWithLink.bind(null, email.value);
        } else if (state.value === ViewState.SignInWithLinkError) {
            try {
                console.log(`Attempting to sign in with existing email: ${email.value}`);
                operationFn = signInWithLink.bind(null, email.value);
            } catch (error) {
                console.log(`Sign in failed so resending email link`);
                operationFn = onSendSignInLink;
            }
        } else if (state.value === ViewState.SignInWithPassword) {
            console.log(`Sign in with existing email and password: ${email.value}`);
            operationFn = signInWithEmail.bind(null, email.value, password.value);
        } else if (state.value === ViewState.RequestResetPassword) {
            operationFn = onRequestResetPassword;
        }

        if (operationFn) {
            await doLoadingOperation(operationFn);
        }
    } catch (error) {
        handleError(error);
    }
};

const onSignInWithProvider = async (provider) => {
    try {
        console.log(`Sign-in with ${provider}`);
        clearForm();
        clearError();
        await signInWithProvider(provider);
    } catch (error) {
        handleError(error);
    }
};

const validatePasswordFields = () => {
    if (password.value === confirmPassword.value) {
        return true;
    }
    confirmPasswordInput.value.invalidate(t('common.error.confirm_password_does_not_match'));
    return false;
};

// https://firebase.google.com/docs/auth/admin/errors
const ERROR_MESSAGE_PREFIX = 'authenticator.alert';
const handleError = (error) => {
    if (!ignoreError(error)) {
        let key;
        if (error.code && error.code.startsWith('auth/')) {
            if (['auth/invalid-email', 'auth/invalid-action-code', 'auth/user-not-found'].includes(error.code)) {
                key = state.value === ViewState.SignInWithPassword ? 'wrong_password' : 'invalid_email';
            } else {
                key = error.code.replace('auth/', '').replace('-', '_');
            }
        }
        let keypath = `${ERROR_MESSAGE_PREFIX}.${key}`;
        if (!key || !te(keypath)) {
            keypath = `${ERROR_MESSAGE_PREFIX}.${error.code ? 'unexpected_error_code' : 'unknown_error'}`;
        }
        error.keypath = keypath;
        err.value = error;
        console.error(error);
    }
};

const initializeTestErrorFromUrl = () => {
    if (config.value.debug) {
        try {
            const errors = getMessagesWithPrefix(ERROR_MESSAGE_PREFIX);
            const keys = Object.keys(errors);
            let msg = `TEST ERROR OPTIONS:\n\n`;
            msg += keys.join('\n');
            msg += `\n\nEXAMPLE USAGE:\n?testError=${keys[0]}\n\n`;
            console.log(msg);

            const searchParams = new URLSearchParams(window.location.search);
            const param = searchParams.get('testError');
            if (param) {
                const keypath = `${ERROR_MESSAGE_PREFIX}.${param}`;
                if (te(keypath)) {
                    console.log(`Using test error: ${keypath}`);
                    testError.value = { keypath, code: 'test', customData: { email: 'fake@ihunterapp.com' } };
                } else {
                    console.error(`Specified test error '${param}' is invalid`);
                }
            } else {
                console.log('No test error was specified');
            }
        } catch (error) {
            console.error(`initializeTestErrorFromUrl`, error);
        }
    }
};

const providers = computed(() => {
    if (err.value?.providers) {
        return supportedProviders.filter((provider) => {
            return err.value?.providers.includes(provider.id);
        });
    }
    return err.value?.providers || supportedProviders;
});

const clearError = () => {
    err.value = testError.value || null;
    setLinkState(ComponentState.Default);
};

const clearForm = () => {
    // email.value = '';
    password.value = '';
    displayName.value = '';
};

const reset = () => {
    clearForm();
    setViewState(ViewState.SignIn);
    // focusField(emailInput);
    setLinkState(ComponentState.Default);
};

const ignoreError = (error) => {
    if (
        !error ||
        // We can safely suppress these errors and don't need to expose them to users
        error.code === 'auth/popup-closed-by-user' ||
        error.code === 'auth/cancelled-popup-request'
    ) {
        return true;
    }
    return false;
};
</script>

<style lang="scss" scoped>
@use 'ihunter-pathfinder/styles' as Pathfinder;

//$unit-max: 390px; moved to $max-form-width in layout.scss

.authenticator {
    // @include Pathfinder.VerticalStack;
    display: flex;
    flex-direction: column;

    a {
        color: var(--action);
        width: max-content;
    }

    a:visited {
        text-decoration: none;
        color: var(--action);
    }
}

.paragraph-content {
    @include Pathfinder.Macro-Text;
    margin-bottom: Pathfinder.$space-300;

    @include Pathfinder.below(min) {
        @include Pathfinder.Macro-Text;
    }
}

.toast-container {
    max-width: Pathfinder.$max-form-width;
    margin-bottom: Pathfinder.$space-200;
}

.subtext {
    @include Pathfinder.Micro-Text;
    margin-top: Pathfinder.$space-100;

    // @include Pathfinder.below(min) {
    //   @include Pathfinder.Micro-Text;
    // }
}

.button-container {
    width: 100%;
    max-width: Pathfinder.$max-form-width;

    .eyebrow-heading {
        @include Pathfinder.Eyebrow;
        text-transform: uppercase;
        display: flex;
        justify-content: center;
        color: var(--on-surface-alt);
        margin-top: Pathfinder.$space-200;
    }
}

form {
    display: flex;
    flex-direction: column;
    gap: Pathfinder.$space-200;

    /* Add space between inputs and buttons */
    .horizontal-stack {
        margin-top: Pathfinder.$space-100;
    }
}

.provider-button {
    border: none;
    padding: Pathfinder.$space-100;
    justify-content: flex-start !important; // won't work without !important

    // &:hover {
    //     // padding: Pathfinder.$space-100 - 1px;
    //     // border: solid 1px var(--action-glow-border);
    // }

    &.email {
        @include Pathfinder.Input-Active;
        text-transform: none;
        letter-spacing: 0.32px;
    }

    &.Google {
        color: #767676;
        border: solid 1px #767676;
        @include Pathfinder.Input-Active;
        letter-spacing: 0.32px;
        text-transform: none;
        background: white;

        &:hover {
            @include Pathfinder.BackgroundBlend(var(--action-glowshit), var(--action-box));
        }

        &:focus {
            @include Pathfinder.BackgroundBlend(var(--action-glowshit), var(--action-box));
        }
    }

    &.Apple {
        @include Pathfinder.Input-Active;
        text-transform: none;
        letter-spacing: 0.32px;
        background-color: black;
        color: white;
    }

    &.Facebook {
        @include Pathfinder.Input-Active;
        text-transform: none;
        letter-spacing: 0.32px;
        background-color: #25467d;
        color: #fff;
    }
}

.legal {
    @include Pathfinder.Legalcopy;
    text-align: left;
    margin-top: Pathfinder.$space-300;
    margin-bottom: Pathfinder.$space-500;
    color: var(--on-surface-alt);
}
</style>
